import React from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../AuthContext';
import { DASHBOARD_STATS_QUERY } from '../graphql/dashboard';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import './Dashboard.css';

interface WorkOrder {
  id: number;
  status: string;
  assignedTo: number | null;
  type: string | null;
  dueAt: string | null;
  completedAt: string | null;
  quoteId: number;
  archivedAt: string | null;
}

interface QuoteLineItemStub {
  id: number;
  quantity: number;
  unitPrice: number;
  archivedAt: string | null;
}

interface QuoteLineStub {
  id: number;
  archivedAt: string | null;
  quoteLineItemsByQuoteLineId: { nodes: QuoteLineItemStub[] };
}

interface Quote {
  id: number;
  status: string;
  createdAt: string;
  archivedAt: string | null;
  quoteLinesByQuoteId: { nodes: QuoteLineStub[] };
}

interface Job {
  id: number;
  archivedAt: string | null;
}

const quoteTotalCents = (quote: Quote): number =>
  quote.quoteLinesByQuoteId.nodes
    .filter((line) => !line.archivedAt)
    .reduce((lineSum, line) => {
      const itemsTotal = line.quoteLineItemsByQuoteLineId.nodes
        .filter((item) => !item.archivedAt)
        .reduce((itemSum, item) => itemSum + Number(item.quantity) * Number(item.unitPrice), 0);
      return lineSum + itemsTotal;
    }, 0);

const formatDollars = (cents: number): string =>
  '$' + Math.round(cents / 100).toLocaleString();

const TYPE_LABELS: Record<string, string> = {
  drapery: 'Drapery',
  roman_shade: 'Roman Shade',
  roller_shade: 'Roller Shade',
};

function StatCard({
  value,
  label,
  accent,
  href,
}: {
  value: number | string;
  label: string;
  accent?: 'amber' | 'red' | 'green' | 'blue';
  href?: string;
}) {
  const colors: Record<string, string> = {
    amber: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    red:   'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    green: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    blue:  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  };
  const bg = colors[accent || 'blue'];
  const content = (
    <div className="stat-card" style={{ background: bg }}>
      <div className="stat-value">{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
  if (href) return <a href={href} style={{ textDecoration: 'none' }}>{content}</a>;
  return content;
}

export default function Dashboard() {
  const { user } = useAuth();

  const { data, loading } = useQuery(DASHBOARD_STATS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });

  const allQuotes: Quote[] = data?.allQuotes?.nodes ?? [];
  const allWorkOrders: WorkOrder[] = data?.allWorkOrders?.nodes ?? [];
  const allJobs: Job[] = data?.allJobs?.nodes ?? [];

  const activeJobs = allJobs.filter(j => !j.archivedAt).length;
  const quotes = allQuotes.filter(q => !q.archivedAt);
  const workOrders = allWorkOrders.filter(wo => !wo.archivedAt);

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
  const in14Days = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const quotesThisMonth = quotes.filter(q => new Date(q.createdAt) >= startOfMonth).length;
  const woQuoteIds = new Set(workOrders.map(wo => wo.quoteId));

  const acceptedPipelineQuotes = quotes.filter(q => q.status.toLowerCase() === 'accepted' && !woQuoteIds.has(q.id));
  const quotesAwaitingWO = acceptedPipelineQuotes.length;
  const pipelineCents = acceptedPipelineQuotes.reduce((sum, q) => sum + quoteTotalCents(q), 0);

  const pendingPipelineQuotes = quotes.filter(q => q.status.toLowerCase() === 'pending' && !woQuoteIds.has(q.id));
  const pendingAwaitingWO = pendingPipelineQuotes.length;
  const pendingPipelineCents = pendingPipelineQuotes.reduce((sum, q) => sum + quoteTotalCents(q), 0);

  const openWOs = workOrders.filter(wo => wo.status !== 'complete');

  // Work orders don't carry their own price — attribute each one the full
  // value of the quote it came from (same convention as the pipeline stats
  // above, which do the same thing for quotes without a work order yet).
  const quoteCentsById = new Map(quotes.map(q => [q.id, quoteTotalCents(q)]));
  const woValueCents = (wo: WorkOrder) => quoteCentsById.get(wo.quoteId) || 0;

  const inProductionCents = workOrders
    .filter(wo => wo.status === 'ready' || wo.status === 'in_progress' || wo.status === 'on_hold')
    .reduce((sum, wo) => sum + woValueCents(wo), 0);

  const completedWOs = workOrders.filter(wo => wo.status === 'complete' && wo.completedAt);
  const completedTodayCents = completedWOs
    .filter(wo => isSameDay(new Date(wo.completedAt!), now))
    .reduce((sum, wo) => sum + woValueCents(wo), 0);
  const completedThisWeekCents = completedWOs
    .filter(wo => new Date(wo.completedAt!) >= startOfWeek)
    .reduce((sum, wo) => sum + woValueCents(wo), 0);
  const completedThisMonthCents = completedWOs
    .filter(wo => new Date(wo.completedAt!) >= startOfMonth)
    .reduce((sum, wo) => sum + woValueCents(wo), 0);

  const unassigned = openWOs.filter(wo => wo.assignedTo == null).length;
  const dueSoon = openWOs.filter(wo => {
    if (!wo.dueAt) return false;
    const d = new Date(wo.dueAt);
    return d >= now && d <= in14Days;
  }).length;
  const myWOs = openWOs.filter(wo => wo.assignedTo === user?.id).length;

  const byType = ['drapery', 'roman_shade', 'roller_shade'].map(t => ({
    label: TYPE_LABELS[t],
    count: openWOs.filter(wo => wo.type === t).length,
  }));

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-content" style={{ textAlign: 'center', paddingTop: 80, color: '#9ca3af' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-content">
        <div className="dashboard-grid">
          <a href="/customers" className="dashboard-card">
            <div className="card-icon"><PeopleIcon fontSize="inherit" /></div>
            <h3>Customers</h3>
            <p>Manage client accounts</p>
          </a>
          <a href="/quotes" className="dashboard-card">
            <div className="card-icon"><AssignmentIcon fontSize="inherit" /></div>
            <h3>Quotes</h3>
            <p>Create and track room quotes</p>
          </a>
          <a href="/work-orders" className="dashboard-card">
            <div className="card-icon"><RocketLaunchIcon fontSize="inherit" /></div>
            <h3>Work Orders</h3>
            <p>Manage traveler sheets</p>
          </a>
        </div>

        {/* ── Key metrics ──────────────────────────────────── */}
        <div className="quick-stats" style={{ marginBottom: 24 }}>
          <h2>Overview</h2>
          <div className="stats-grid">
            <StatCard
              value={formatDollars(pipelineCents)}
              label={`Sales Pipeline — ${quotesAwaitingWO} ${quotesAwaitingWO === 1 ? 'Quote' : 'Quotes'} Awaiting Work Orders`}
              accent={quotesAwaitingWO > 0 ? 'amber' : 'blue'}
              href="/quotes"
            />
            <StatCard
              value={formatDollars(pendingPipelineCents)}
              label={`Pending Pipeline — ${pendingAwaitingWO} ${pendingAwaitingWO === 1 ? 'Quote' : 'Quotes'} Awaiting Response`}
              accent="blue"
              href="/quotes"
            />
            <StatCard value={openWOs.length} label="Open Work Orders" href="/work-orders" />
            <StatCard value={activeJobs} label="Active Jobs" href="/jobs" />
            <StatCard value={quotesThisMonth} label="Quotes This Month" href="/quotes" />
          </div>
        </div>

        {/* ── Production value ────────────────────────────── */}
        <div className="quick-stats" style={{ marginBottom: 24 }}>
          <h2>Production Value</h2>
          <div className="stats-grid">
            <StatCard
              value={formatDollars(inProductionCents)}
              label="Ready / In Progress / On Hold"
              accent="blue"
              href="/work-orders"
            />
            <StatCard
              value={formatDollars(completedTodayCents)}
              label="Completed Today"
              accent="green"
              href="/work-orders"
            />
            <StatCard
              value={formatDollars(completedThisWeekCents)}
              label="Completed This Week"
              accent="green"
              href="/work-orders"
            />
            <StatCard
              value={formatDollars(completedThisMonthCents)}
              label="Completed This Month"
              accent="green"
              href="/work-orders"
            />
          </div>
        </div>

        {/* ── Action items ─────────────────────────────────── */}
        <div className="quick-stats" style={{ marginBottom: 24 }}>
          <h2>Action Items</h2>
          <div className="stats-grid">
            <StatCard
              value={unassigned}
              label="Unassigned Work Orders"
              accent={unassigned > 0 ? 'red' : 'green'}
              href="/work-orders"
            />
            <StatCard
              value={dueSoon}
              label="Due Within 14 Days"
              accent={dueSoon > 0 ? 'amber' : 'green'}
              href="/work-orders"
            />
            <StatCard
              value={myWOs}
              label="My Open Work Orders"
              accent={myWOs > 0 ? 'blue' : 'green'}
              href="/work-orders"
            />
          </div>
        </div>

        {/* ── Work order type breakdown ─────────────────────── */}
        <div className="quick-stats">
          <h2>Open Work Orders by Type</h2>
          <div className="stats-grid">
            {byType.map(({ label, count }) => (
              <StatCard key={label} value={count} label={label} href="/work-orders" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
